import apiServiceGenerator from "@/api/generator";
import {changes, loading, alert, handleError} from './helpers'

export default function (endpoint, /*namespace*/) {

    let pending = {};

    function normalizeFilters(filters) {
        return JSON.stringify(Object.keys(filters).sort().reduce((acc, key) => {
            acc[key] = filters[key];
            return acc;
        }, {}))
    }

    return {
        namespaced: true,

        state: {
            loading: false,
            pager: {},
            additional: {},
            list: [],
            all: [],
            allQuery: {},
            item: {},
            errors: {},
            fields: [],
            service: apiServiceGenerator(endpoint),
            cache: {}
        },

        getters: {
            loading: function (state) {
                return !!state.loading
            },
            pager: function (state) {
                return JSON.parse(JSON.stringify(state.pager))
            },
            all: function (state) {
                return JSON.parse(JSON.stringify(state.all))
            },
            list: function (state) {
                return state.list
            },
            item: function (state) {
                return JSON.parse(JSON.stringify(state.item))
            },
            additional: function (state) {
                return JSON.parse(JSON.stringify(state.additional))
            },
            states: function (state) {
                const states = {};

                Object.keys(state.errors).forEach(key => {
                        states[key] = false
                    }
                )

                return states
            },
            ordered: (state) => (prop) => {
                return JSON.parse(JSON.stringify(state.list)).sort(function (a,b) {
                    return a[prop] < b[prop]
                        ? -1
                        : (
                            a[prop] > b[prop]
                            ? 1
                            : 0
                        )
                })
            },
            errors: function (state) {
                const errors = Object.assign({}, (state.errors || {}))

                Object.keys(errors).forEach(key => {
                    errors[key] = errors[key].join(' ')
                })

                return errors;
            },
            fields: function (state) {
                return state.fields
            },
        },

        mutations: {
            'SET_LOADING': function (state, boolean = true) {
                state.loading = boolean
            },

            'SET_ALL_QUERY': function (state, query = {}) {
                state.allQuery = query
            },

            'SET_ALL': function (state, all) {
                state.all = all
            },

            'SET_LIST': function (state, list) {
                state.list = list
            },

            'SET_ITEM': function (state, item = {}) {
                state.item = item
            },

            'UPDATE_ITEM': function (state, props) {
                Object.keys(props).forEach(prop => {
                    state.item[prop] = props[prop]
                })
            },

            'ADD_ITEM': function (state, item) {
                if (!state.list) {
                    state.list = [];
                }

                state.list.unshift(item)
            },

            'PUSH_ITEM': function (state, item) {
                if (!state.list) {
                    state.list = []
                }

                state.list.push(item)
                state.all.push(item)
            },

            'PUSH_ITEM_INTO_ALL': function (state, item) {
                if (!state.all) {
                    state.all = []
                }

                state.all.push(item);
            },

            'REPLACE_ITEM': function (state, item) {
                const index = state.list.findIndex(listItem => listItem.id === item.id);

                if (index !== -1) {
                    state.list[index] = item
                    state.list = JSON.parse(JSON.stringify(state.list))
                }

                const allIndex = state.all.findIndex(allItem => allItem.id === item.id);

                if (allIndex !== -1) {
                    state.all[allIndex] = item
                    state.all = JSON.parse(JSON.stringify(state.all))
                }
            },

            'REMOVE_ITEM': function (state, id) {
                const index = state.list.findIndex(item => item.id === id);

                if (index !== -1) {
                    state.list.splice(index, 1)
                }

                const allIndex = state.all.findIndex(item => item.id === id);

                if (allIndex !== -1) {
                    state.all.splice(index, 1)
                }
            },

            'SET_PAGER': function (state, pager = {}) {
                state.pager = pager
            },

            'SET_ADDITIONAL': function (state, data = {}) {
                state.additional = data
            },

            'SET_ERRORS'(state, errors) {
                state.errors = errors
            },

            'SET_ERROR'(state, error) {
                const errors = Object.assign({}, state.errors)

                Object.keys(error).forEach(key => {
                    errors[key]
                        ? errors[key].push(error[key])
                        : errors[key] = Array(error[key])
                })

                state.errors = errors
            },

            'SET_FIELDS': function(state, fields) {
                state.fields = fields
            },

            'ADD_TO_CACHE': function (state, {key, result}) {
                state.cache[key] = {
                    result: result.data,
                    additional: result.additional,
                    meta: result.data
                };
            },
        },

        actions: {
            all: function ({commit, state, getters}, force) {
                if (!state.all || !state.all.length || force) {

                    let data = typeof force === 'object' ? force : undefined;

                    const filterKey = data ? normalizeFilters(data) : 'empty';

                    if (state.cache[filterKey]) {
                        return state.cache[filterKey];
                    }

                    loading(commit);

                    if (pending[filterKey]) {
                        return pending[filterKey];
                    }

                    if (data) {
                        commit('SET_ALL_QUERY', data);
                    }
                    const request = state.service.fetchAll(data).then(response => {
                        commit('ADD_TO_CACHE', {key: filterKey, result: response});
                        commit('SET_ALL', response.data)
                        loading(commit, false)
                        return response.data
                    }).catch((e) => {
                        pending = null;
                        handleError(e, commit);
                    });

                    pending[filterKey] = request;

                    return request;
                } else {
                    return getters.all;
                }
            },

            list: function ({commit, state}, filters) {
                loading(commit)

                return state.service.fetchList(filters).then(response => {
                    commit('SET_LIST', response.data)
                    commit('SET_PAGER', response.meta)
                    commit('SET_ADDITIONAL', response.additional)
                    loading(commit, false)
                    return response.data
                }).catch((e) => {
                    handleError(e, commit)
                })
            },

            show: function ({commit, state}, {id, query}) {
                loading(commit)

                return state.service.fetchItem(id, query).then(data => {
                    commit('SET_ITEM', data.data)
                    loading(commit, false)
                    return data
                }).catch((e) => {
                    handleError(e, commit)
                })
            },

            select: function ({commit, state}, id) {
                loading(commit)

                let item = state.list.find(item => item.id === id);

                if (!item) {
                    state.all.find(item => item.id === id)
                }

                if (item) {
                    commit('SET_ITEM', item);
                    loading(commit, false)
                    return item;
                } else {
                    loading(commit, false)
                }
            },

            create: function ({commit, state, /*dispatch*/}, payload) {
                loading(commit)

                return state.service.store(payload).then(data => {
                    const model = Object.assign({}, payload, data.data);

                    commit('ADD_ITEM', model);
                    commit('SET_ITEM', model);
                    alert(commit, this.$t('request.creation_is_successful'));
                    commit('SET_ERRORS', {});
                    // dispatch('all', state.allQuery);

                    loading(commit, false)

                    return model;
                }).catch((e) => {
                    handleError(e, commit)
                })
            },

            addToAll({commit}, item) {
                commit('PUSH_ITEM_INTO_ALL', item);
            },

            createConfig: function ({commit, state, dispatch}, { push, payload}) {
                loading(commit)

                return state.service.store(payload).then(data => {
                    const model = Object.assign({}, payload, data.data)

                    commit(push ? 'PUSH_ITEM' : 'ADD_ITEM', model)
                    commit('SET_ITEM', model)
                    alert(commit, this.$t('request.creation_is_successful'))
                    commit('SET_ERRORS', {});
                    dispatch('all', true);

                    loading(commit, false)

                    return model
                }).catch((e) => {
                    handleError(e, commit)
                })
            },

            update: function ({commit, state}, payload) {
                loading(commit)

                const { id, data } = changes(payload, state.item)

                const content = Object.assign({_method: 'PUT'}, data);
                return state.service.update(id, content).then(response => {
                    const model = Object.assign({}, data, response.data)

                    commit('REPLACE_ITEM', model);
                    commit('SET_ITEM', model);
                    alert(commit, this.$t('request.save_is_successful'))
                    commit('SET_ERRORS', {});
                    loading(commit, false)

                    return model;
                }).catch((e) => {
                    handleError(e, commit)
                })
            },

            destroy: function ({commit, state}, id) {
                loading(commit)

                return state.service.destroy(id).then(response => {
                    commit('REMOVE_ITEM', id);
                    commit('SET_ITEM', {});
                    alert(commit, this.$t('request.deletion_is_successful'))
                    loading(commit, false)

                    return response;
                }).catch((e) => {
                    handleError(e, commit)
                });
            },

            push: function ({commit}, item) {
                commit('PUSH_ITEM', item)
            },

            setItem: function ({commit}, payload) {
                commit('SET_ITEM', payload)
            },

            setAll: function ({commit}, payload) {
                commit('SET_ALL', payload)
            },

            clearItem: function ({commit}) {
                commit('SET_ITEM')
            },

            clearList: function ({commit}) {
                commit('SET_LIST', [])
                commit('SET_PAGER', {})
                commit('SET_ADDITIONAL', {})
            },

            clearAll: function ({commit}) {
                commit('SET_ALL', [])
            },

            clearErrors: function ({commit}) {
                commit('SET_ERRORS', {})
            },

            clearModule: function ({dispatch}) {
                dispatch('clearItem')
                dispatch('clearList')
                // dispatch('clearAll')
                dispatch('clearErrors')
            },

            replaceItem: function ({commit}, item) {
                commit('REPLACE_ITEM', item);
            },

            fields: function ({commit, rootState}, payload) {
                loading(commit)

                return rootState.field.service.fetchList(payload).then(response => {
                    commit('SET_FIELDS', response.data)
                    loading(commit, false)
                    return response.data
                }).catch((e) => {
                    handleError(e, commit)
                })
            },
        },
    }
}
